// memorybox studio — the creator dashboard.
// Sign in (Google or email), create & co-own boxes, moderate notes,
// export keepsake emails, and read a friction-focused analytics view.

import { STUDIO_CONFIG as CFG } from "./config.ts";
import type { Board, Note } from "@ts/core/types.ts";
import { STICKERS, stickerArt } from "@ts/core/types.ts";

const { createClient } = await import(
  /* @vite-ignore */ "https://esm.sh/@supabase/supabase-js@2" as string
);
const db = createClient(CFG.supabaseUrl, CFG.supabaseAnonKey);

const $ = (id: string) => document.getElementById(id)!;
const app = $("app");
const el = (tag: string, cls?: string, text?: string) => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
};

// ── friendly slug: quiet-river-3f7a ───────────────────────────────
const ADJ = ["quiet", "warm", "golden", "soft", "amber", "tender", "still", "bright", "gentle", "little"];
const NOUN = ["river", "room", "evening", "candle", "letter", "harbour", "meadow", "lantern", "echo", "garden"];
const rand = <T,>(a: T[]) => a[Math.floor(Math.random() * a.length)];
const hex = (n: number) =>
  [...crypto.getRandomValues(new Uint8Array(n))].map((b) => b.toString(16).padStart(2, "0")).join("");
const makeSlug = () => `${rand(ADJ)}-${rand(NOUN)}-${hex(2)}`;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
const isFrozen = (b: Board) => b.frozen_early || Date.now() >= new Date(b.freeze_at).getTime();

// default freeze: 10 days out, 23:59 IST
function defaultFreeze(): string {
  const d = new Date(Date.now() + 10 * 864e5);
  return d.toISOString().slice(0, 10) + "T23:59:59+05:30";
}

// ── auth ──────────────────────────────────────────────────────────
async function currentUser() {
  const { data } = await db.auth.getUser();
  return data.user ?? null;
}

async function claimInvites(email: string) {
  const { data: invites } = await db.from("board_invites").select("*").eq("email", email);
  if (!invites?.length) return;
  const user = await currentUser();
  if (!user) return;
  for (const inv of invites) {
    await db.from("board_members").insert({ board_id: inv.board_id, user_id: user.id, role: inv.role });
    await db.from("board_invites").delete().eq("id", inv.id);
  }
}

// ── views ─────────────────────────────────────────────────────────
function clear() {
  app.innerHTML = "";
}

function renderSignIn() {
  clear();
  $("whoami").hidden = true;
  const card = el("section", "card sign-in");
  card.append(
    el("h1", "", "make a memorybox"),
    el(
      "p",
      "lede",
      "a warm keepsake board for any gathering - a listening session, a wedding, a farewell. create one, share the link with the people who were there, and watch it fill."
    )
  );

  const gbtn = el("button", "btn primary", "continue with Google") as HTMLButtonElement;
  gbtn.onclick = () =>
    db.auth.signInWithOAuth({ provider: "google", options: { redirectTo: location.href.split("#")[0] } });

  const orRow = el("div", "or", "or with email");
  const email = el("input") as HTMLInputElement;
  email.type = "email";
  email.placeholder = "you@email.com";
  const ebtn = el("button", "btn", "email me a sign-in link") as HTMLButtonElement;
  const note = el("p", "hint");
  ebtn.onclick = async () => {
    const addr = email.value.trim();
    if (!addr) return;
    ebtn.disabled = true;
    const { error } = await db.auth.signInWithOtp({
      email: addr,
      options: { emailRedirectTo: location.href.split("#")[0] },
    });
    ebtn.disabled = false;
    note.textContent = error ? `hmm: ${error.message}` : "check your inbox for the link 🤍";
  };

  card.append(gbtn, orRow, email, ebtn, note);
  app.append(card);
}

async function renderBoards() {
  clear();
  const user = await currentUser();
  if (!user) return renderSignIn();
  $("whoami").hidden = false;
  $("whoami-email").textContent = user.email ?? "signed in";

  const { data: memberships } = await db
    .from("board_members")
    .select("board_id, role, boards(*)")
    .order("created_at", { ascending: false });
  const boards: Board[] = (memberships ?? []).map((m: any) => m.boards).filter(Boolean);

  const head = el("div", "boards-head");
  head.append(el("h1", "", "your memoryboxes"));
  const create = el("button", "btn primary", "+ new box") as HTMLButtonElement;
  create.onclick = renderCreate;
  head.append(create);
  app.append(head);

  if (!boards.length) {
    app.append(el("p", "empty", "no boxes yet - make your first one 🤍"));
    return;
  }

  const grid = el("div", "board-grid");
  for (const b of boards) {
    const c = el("button", "board-card") as HTMLButtonElement;
    c.onclick = () => renderBoard(b.id);
    const status = isFrozen(b) ? "frozen" : "open";
    c.append(
      el("span", `chip ${status}`, status),
      el("h2", "", b.title),
      el("p", "board-sub", b.subtitle || b.tagline),
      el("p", "board-meta", `freezes ${fmtDate(b.freeze_at)}`)
    );
    grid.append(c);
  }
  app.append(grid);
}

async function renderCreate() {
  clear();
  const card = el("section", "card");
  card.append(el("h1", "", "new memorybox"));

  const f = {
    title: field(card, "title", "memorybox"),
    subtitle: field(card, "subtitle (the occasion)", "a evening in Chennai · 16 July 2026"),
    tagline: field(card, "tagline", "notes from the room"),
    intro: area(card, "intro / welcome message", "Thank you for being there. Leave behind what it felt like - a note, a photo, a voice."),
    word: field(card, "room word (only people who were there know it)", ""),
    freeze: field(card, "freezes on", defaultFreeze().slice(0, 10)),
  };
  (f.freeze as HTMLInputElement).type = "date";

  const notesCap = field(card, "notes per person", "3");
  const stickCap = field(card, "stickers per person", "6");

  const err = el("p", "hint");
  const go = el("button", "btn primary", "create the box") as HTMLButtonElement;
  go.onclick = async () => {
    go.disabled = true;
    err.textContent = "";
    const user = await currentUser();
    if (!user) return renderSignIn();
    const slug = makeSlug();
    const freezeIso =
      (f.freeze as HTMLInputElement).value
        ? (f.freeze as HTMLInputElement).value + "T23:59:59+05:30"
        : defaultFreeze();
    const { data: board, error } = await db
      .from("boards")
      .insert({
        slug,
        created_by: user.id,
        title: val(f.title) || "memorybox",
        tagline: val(f.tagline) || "notes from the room",
        subtitle: val(f.subtitle),
        intro_line: val(f.intro),
        room_word: val(f.word) || null,
        freeze_at: freezeIso,
        max_notes_per_person: parseInt(val(notesCap)) || 3,
        max_stickers_per_person: parseInt(val(stickCap)) || 6,
      })
      .select()
      .single();
    if (error) {
      go.disabled = false;
      err.textContent = error.message;
      return;
    }
    await db.from("board_members").insert({ board_id: board.id, user_id: user.id, role: "owner" });
    renderBoard(board.id);
  };

  const back = el("button", "link-btn", "← back") as HTMLButtonElement;
  back.onclick = renderBoards;
  card.append(go, err, back);
  app.append(card);
}

async function renderBoard(id: string) {
  clear();
  const { data: board } = await db.from("boards").select("*").eq("id", id).single();
  if (!board) return renderBoards();
  const b = board as Board;

  const back = el("button", "link-btn", "← all boxes") as HTMLButtonElement;
  back.onclick = renderBoards;
  app.append(back);

  const head = el("div", "board-detail-head");
  head.append(el("h1", "", b.title), el("span", `chip ${isFrozen(b) ? "frozen" : "open"}`, isFrozen(b) ? "frozen" : "open"));
  app.append(head);

  // ── share link ──
  const link = `${CFG.guestBase}${b.slug}`;
  const share = el("div", "card share");
  share.append(el("h3", "", "share this box"));
  const linkRow = el("div", "link-row");
  const linkInput = el("input", "", "") as HTMLInputElement;
  linkInput.value = link;
  linkInput.readOnly = true;
  const copy = el("button", "btn", "copy link") as HTMLButtonElement;
  copy.onclick = () => {
    navigator.clipboard.writeText(link);
    copy.textContent = "copied 🤍";
    setTimeout(() => (copy.textContent = "copy link"), 1500);
  };
  linkRow.append(linkInput, copy);
  share.append(linkRow);
  if (b.room_word) share.append(el("p", "hint", `room word: share “${b.room_word}” separately from the link`));
  app.append(share);

  // ── tabs ──
  const tabs = el("div", "tabs");
  const body = el("div", "tab-body");
  const mk = (name: string, fn: () => void) => {
    const t = el("button", "tab", name) as HTMLButtonElement;
    t.onclick = () => {
      [...tabs.children].forEach((c) => c.classList.remove("on"));
      t.classList.add("on");
      body.innerHTML = "";
      fn();
    };
    tabs.append(t);
    return t;
  };
  mk("analytics", () => renderAnalytics(b, body));
  mk("notes", () => renderModeration(b, body));
  mk("settings", () => renderSettings(b, body));
  mk("keepsake emails", () => renderEmails(b, body));
  mk("co-creators", () => renderMembers(b, body));
  app.append(tabs, body);
  (tabs.firstChild as HTMLButtonElement).click();
}

// ── analytics: is anyone struggling? ──────────────────────────────
async function renderAnalytics(b: Board, body: HTMLElement) {
  body.append(el("p", "hint", "loading…"));
  const { data: events } = await db.from("events").select("name, visitor, props").eq("board_id", b.id);
  const { count: noteCount } = await db
    .from("notes")
    .select("id", { count: "exact", head: true })
    .eq("board_id", b.id);
  body.innerHTML = "";

  const evs = events ?? [];
  const visitors = new Set(evs.map((e: any) => e.visitor));
  const count = (n: string) => evs.filter((e: any) => e.name === n).length;
  const posters = new Set(evs.filter((e: any) => e.name === "note_created").map((e: any) => e.visitor));

  const views = count("page_view");
  const rejects = count("room_word_rejected");
  const conversion = visitors.size ? Math.round((posters.size / visitors.size) * 100) : 0;

  const stats = el("div", "stat-grid");
  const stat = (n: number | string, label: string, hint?: string) => {
    const s = el("div", "stat");
    s.append(el("div", "stat-n", String(n)), el("div", "stat-l", label));
    if (hint) s.append(el("div", "stat-hint", hint));
    stats.append(s);
  };
  stat(visitors.size, "people visited");
  stat(posters.size, "people posted", `${conversion}% of visitors`);
  stat(noteCount ?? 0, "notes on the board");
  stat(count("voice_recorded"), "voice notes");
  stat(count("photo_added"), "photos");
  stat(count("sticker_added"), "stickers");
  stat(count("dictation_used"), "used speak-to-type");
  body.append(stats);

  // friction — the thing you asked for
  const friction = el("div", "card friction");
  friction.append(el("h3", "", "friction watch"));
  const notes: string[] = [];
  if (rejects > 0) notes.push(`⚠️ ${rejects} room-word rejections — people may have the wrong word, or spacing/caps trouble (the gate forgives those, so more likely the wrong word is circulating).`);
  const visitedNotPosted = visitors.size - posters.size;
  if (visitors.size >= 5 && conversion < 40)
    notes.push(`⚠️ ${visitedNotPosted} visited but didn't post (${conversion}% conversion) — the compose step may feel unclear, or the room-word gate is stopping them.`);
  if (count("note_created") === 0 && views > 3)
    notes.push(`⚠️ ${views} views, no notes yet — worth checking the link + room word on a phone yourself.`);
  if (!notes.length) notes.push("✅ nothing looks stuck — people are visiting and posting smoothly.");
  notes.forEach((n) => friction.append(el("p", "friction-line", n)));
  body.append(friction);

  await renderActivity(b, body);
}

// ── activity flow: when do people actually post? ──────────────────
async function renderActivity(b: Board, body: HTMLElement) {
  const { data } = await db
    .from("notes")
    .select("created_at")
    .eq("board_id", b.id)
    .order("created_at", { ascending: true });
  const rows = data ?? [];

  const card = el("div", "card activity");
  card.append(el("h3", "", "activity flow"));
  if (!rows.length) {
    card.append(el("p", "hint", "no notes yet — the timeline fills as people post."));
    body.append(card);
    return;
  }

  // bucket notes by day, from board creation to freeze
  const dayMs = 864e5;
  const start = new Date(new Date(b.created_at).toDateString()).getTime();
  const freeze = new Date(b.freeze_at).getTime();
  const end = Math.max(freeze, Date.now());
  const days = Math.min(60, Math.max(1, Math.round((end - start) / dayMs) + 1));
  const buckets = new Array(days).fill(0);
  for (const r of rows) {
    const i = Math.floor((new Date(r.created_at).getTime() - start) / dayMs);
    if (i >= 0 && i < days) buckets[i]++;
  }
  const max = Math.max(...buckets, 1);
  const freezeDay = Math.floor((freeze - start) / dayMs);

  const chart = el("div", "chart");
  buckets.forEach((n, i) => {
    const col = el("div", "bar-col");
    const bar = el("div", "bar");
    bar.style.height = `${(n / max) * 100}%`;
    if (i === freezeDay) col.classList.add("freeze-day");
    // the last 3 days before freeze — the "last-minute" window
    if (i <= freezeDay && i > freezeDay - 3) bar.classList.add("late");
    bar.title = `${n} note${n === 1 ? "" : "s"}`;
    col.append(bar);
    chart.append(col);
  });
  card.append(chart);

  // a plain-language read on the surge
  const last3 = buckets.slice(Math.max(0, freezeDay - 2), freezeDay + 1).reduce((a, c) => a + c, 0);
  const total = buckets.reduce((a, c) => a + c, 0);
  if (total > 0) {
    const pct = Math.round((last3 / total) * 100);
    const daysLeft = Math.ceil((freeze - Date.now()) / dayMs);
    const line =
      daysLeft > 0
        ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} until freeze. ${
            pct >= 40 ? `a lot of notes tend to land in the final days — a reminder could nudge the last-minute folks 🤍` : ""
          }`
        : `${pct}% of notes arrived in the final 3 days — the last-minute surge is real.`;
    card.append(el("p", "hint", line));
  }
  body.append(card);
}

// ── moderation ────────────────────────────────────────────────────
async function renderModeration(b: Board, body: HTMLElement) {
  body.append(el("p", "hint", "loading…"));
  const { data } = await db
    .from("notes")
    .select("*")
    .eq("board_id", b.id)
    .order("created_at", { ascending: false });
  body.innerHTML = "";
  const notes = (data ?? []) as Note[];
  if (!notes.length) return body.append(el("p", "empty", "no notes yet 🤍"));

  for (const n of notes) {
    const row = el("div", "mod-row" + (n.hidden ? " hidden" : ""));
    const main = el("div", "mod-main");
    if (n.kind === "sticker") main.append(el("span", "mod-sticker", stickerArt(n.message) ? "🌸 sticker" : n.message));
    else {
      main.append(el("p", "mod-msg", n.message || "(no text)"));
      const meta = `${n.name || "anon"} · ${new Date(n.created_at).toLocaleString()}` +
        (n.photo_path ? " · 📷" : "") + (n.audio_path ? " · 🎙" : "");
      main.append(el("p", "mod-meta", meta));
    }
    const acts = el("div", "mod-acts");
    const hide = el("button", "link-btn", n.hidden ? "unhide" : "hide") as HTMLButtonElement;
    hide.onclick = async () => {
      await db.from("notes").update({ hidden: !n.hidden }).eq("id", n.id);
      renderModeration(b, body);
    };
    const del = el("button", "link-btn danger", "delete") as HTMLButtonElement;
    del.onclick = async () => {
      if (!confirm("Delete this permanently?")) return;
      const paths = [n.photo_path, n.audio_path].filter(Boolean) as string[];
      if (paths.length) await db.storage.from("board-media").remove(paths);
      await db.from("notes").delete().eq("id", n.id);
      renderModeration(b, body);
    };
    acts.append(hide, del);
    row.append(main, acts);
    body.append(row);
  }
}

// ── settings ──────────────────────────────────────────────────────
async function renderSettings(b: Board, body: HTMLElement) {
  const f = {
    title: field(body, "title", b.title),
    subtitle: field(body, "subtitle", b.subtitle),
    tagline: field(body, "tagline", b.tagline),
    intro: area(body, "intro", b.intro_line),
    word: field(body, "room word", b.room_word ?? ""),
    freeze: field(body, "freezes on", b.freeze_at.slice(0, 10)),
    notes: field(body, "notes per person", String(b.max_notes_per_person)),
    stick: field(body, "stickers per person", String(b.max_stickers_per_person)),
  };
  (f.freeze as HTMLInputElement).type = "date";
  const msg = el("p", "hint");
  const save = el("button", "btn primary", "save changes") as HTMLButtonElement;
  save.onclick = async () => {
    save.disabled = true;
    const { error } = await db
      .from("boards")
      .update({
        title: val(f.title),
        subtitle: val(f.subtitle),
        tagline: val(f.tagline),
        intro_line: val(f.intro),
        room_word: val(f.word) || null,
        freeze_at: (f.freeze as HTMLInputElement).value + "T23:59:59+05:30",
        max_notes_per_person: parseInt(val(f.notes)) || 3,
        max_stickers_per_person: parseInt(val(f.stick)) || 6,
      })
      .eq("id", b.id);
    save.disabled = false;
    msg.textContent = error ? error.message : "saved 🤍";
  };
  body.append(save, msg);

  const freeze = el("button", "btn danger", isFrozen(b) ? "un-freeze the box" : "freeze the box now") as HTMLButtonElement;
  freeze.onclick = async () => {
    await db.from("boards").update({ frozen_early: !b.frozen_early }).eq("id", b.id);
    renderBoard(b.id);
  };
  body.append(el("hr"), freeze);
}

// ── keepsake emails ───────────────────────────────────────────────
async function renderEmails(b: Board, body: HTMLElement) {
  const { data } = await db.from("keepsake_emails").select("email").eq("board_id", b.id);
  const emails = [...new Set((data ?? []).map((r: any) => r.email))];
  body.append(el("p", "hint", `${emails.length} people want the frozen box`));
  const box = el("textarea", "emails") as HTMLTextAreaElement;
  box.readOnly = true;
  box.value = emails.join("\n");
  body.append(box);
  const copy = el("button", "btn", "copy all") as HTMLButtonElement;
  copy.onclick = () => navigator.clipboard.writeText(emails.join(", "));
  body.append(copy);
}

// ── co-creators ───────────────────────────────────────────────────
async function renderMembers(b: Board, body: HTMLElement) {
  const { data: members } = await db.from("board_members").select("user_id, role").eq("board_id", b.id);
  const { data: invites } = await db.from("board_invites").select("email, role").eq("board_id", b.id);
  body.append(el("p", "hint", `${(members ?? []).length} co-creator(s), ${(invites ?? []).length} pending invite(s)`));
  (invites ?? []).forEach((i: any) => body.append(el("p", "mod-meta", `pending: ${i.email}`)));

  const inp = el("input") as HTMLInputElement;
  inp.type = "email";
  inp.placeholder = "co-creator's email";
  const add = el("button", "btn", "invite") as HTMLButtonElement;
  const msg = el("p", "hint");
  add.onclick = async () => {
    const email = inp.value.trim().toLowerCase();
    if (!email) return;
    const { error } = await db.from("board_invites").insert({ board_id: b.id, email });
    msg.textContent = error ? error.message : `invited ${email} — they'll see this box when they sign in with that email 🤍`;
    inp.value = "";
    renderMembers(b, body);
  };
  body.append(inp, add, msg);
}

// ── tiny form helpers ─────────────────────────────────────────────
function field(parent: HTMLElement, label: string, value: string) {
  parent.append(el("label", "flabel", label));
  const i = el("input") as HTMLInputElement;
  i.value = value;
  parent.append(i);
  return i;
}
function area(parent: HTMLElement, label: string, value: string) {
  parent.append(el("label", "flabel", label));
  const t = el("textarea") as HTMLTextAreaElement;
  t.value = value;
  parent.append(t);
  return t;
}
const val = (e: HTMLInputElement | HTMLTextAreaElement) => e.value.trim();

// ── boot ──────────────────────────────────────────────────────────
$("signout").onclick = async () => {
  await db.auth.signOut();
  renderSignIn();
};

db.auth.onAuthStateChange(async (_evt: string, session: any) => {
  if (session?.user?.email) await claimInvites(session.user.email);
  session ? renderBoards() : renderSignIn();
});

const user = await currentUser();
if (user) {
  if (user.email) await claimInvites(user.email);
  renderBoards();
} else {
  renderSignIn();
}
